/**
 * MCQOptionsEditor — collapsible MCQ option-rows block.
 *
 * Self-contained: owns its expand/collapse, per-row image-upload UX (the
 * upload zone, error string, crop modal). Receives the options + correct
 * answer as controlled value/onChange props and emits both back to the
 * parent — single-select MCQ flips other options' `isCorrect` to false
 * when one is set, multi-select preserves toggles.
 *
 * Extracted from `QuestionEditForm.tsx` during the 2026-05-17 split so
 * its substantial UI state (~4 hooks) doesn't bloat the form shell.
 */
import { useRef, useState } from 'react';
import type { MCQOption } from '../../questions-page';
import { LatexEditor } from '../../latex';
import { ImageViewer } from '../../components';
import { ImageCropModal } from '../../image-editor';
import { MCQ_LABELS } from '../formTypes';
import type { QuestionImageSlot } from '@padcanvas/shared';
import { useQuestionSource } from '../questionSource';

export interface MCQOptionsEditorProps {
    options: MCQOption[];
    isMulti: boolean;
    onOptionsChange: (options: MCQOption[]) => void;
    /** Called alongside `onOptionsChange` whenever the correct-answer set
     *  changes — the parent stores this as comma-joined uppercase labels. */
    onCorrectAnswerChange: (correctAnswer: string) => void;
    onUploadImage?: (base64: string, mimeType: string) => Promise<{ url: string }>;
    /** Which image slot an option's image is, so it can be cut from the source
     *  paper. Omitted ⇒ no "from paper" action. */
    imageSlot?: (label: string) => QuestionImageSlot;
}

export function MCQOptionsEditor({
    options,
    isMulti,
    onOptionsChange,
    onCorrectAnswerChange,
    onUploadImage,
    imageSlot,
}: MCQOptionsEditorProps) {
    const source = useQuestionSource();
    const fromPaper = source && imageSlot
        ? (label: string) => { setImageOpen(null); source.openCrop(imageSlot(label)); }
        : undefined;
    const [expanded, setExpanded] = useState(options.length > 0);
    const [imageOpen, setImageOpen] = useState<number | null>(null);
    const [imageUploading, setImageUploading] = useState(false);
    const [imageError, setImageError] = useState('');
    const [cropIdx, setCropIdx] = useState<number | null>(null);
    const fileRef = useRef<HTMLInputElement>(null);

    const handleOptionChange = (index: number, field: keyof MCQOption, value: string | undefined) => {
        const next = [...options];
        next[index] = { ...next[index], [field]: value };
        onOptionsChange(next);
    };

    const addOption = () => {
        // Existing options can carry lowercase labels (older/extracted data) —
        // display already normalizes with `.toUpperCase()`, so the "used" check
        // has to as well, or a lowercase 'a'/'b' never reads as taken and the
        // next option collides back to 'A'/'B' instead of continuing to C/D.
        const usedLabels = options.map(o => o.label.toUpperCase());
        const nextLabel = MCQ_LABELS.find(l => !usedLabels.includes(l)) || MCQ_LABELS[options.length] || 'X';
        onOptionsChange([...options, { label: nextLabel, text: '' }]);
    };

    const removeOption = (index: number) => {
        const next = [...options];
        next.splice(index, 1);
        onOptionsChange(next);
    };

    const toggleCorrect = (idx: number) => {
        const updated = isMulti
            ? options.map((o, i) => i === idx ? { ...o, isCorrect: !o.isCorrect } : o)
            : options.map((o, i) => ({ ...o, isCorrect: i === idx }));
        onOptionsChange(updated);
        const correctLabels = updated.filter(o => o.isCorrect).map(o => o.label.toUpperCase());
        onCorrectAnswerChange(correctLabels.join(','));
    };

    const uploadOptionImage = async (file: File, idx: number) => {
        if (!onUploadImage) return;
        if (!file.type.startsWith('image/')) {
            setImageError('Not an image file');
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            setImageError('Image too large (max 10MB)');
            return;
        }
        setImageUploading(true);
        setImageError('');
        try {
            const base64 = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve((reader.result as string).split(',')[1]);
                reader.onerror = reject;
                reader.readAsDataURL(file);
            });
            const { url } = await onUploadImage(base64, file.type);
            handleOptionChange(idx, 'imageUrl', url);
            setImageOpen(null);
        } catch (err) {
            setImageError(err instanceof Error ? err.message : 'Upload failed');
        } finally {
            setImageUploading(false);
        }
    };

    return (
        <div className="form-section collapsible">
            <div className="collapsible-header" onClick={() => setExpanded(e => !e)}>
                <span className="collapse-icon">{expanded ? '▼' : '▶'}</span>
                <label>MCQ Options ({options.length})</label>
                <button
                    type="button"
                    className="btn-add-inline"
                    onClick={e => { e.stopPropagation(); addOption(); setExpanded(true); }}
                >
                    + Add
                </button>
            </div>
            {expanded && (
                <div className="collapsible-body">
                    {options.map((option, idx) => (
                        <div key={idx} className={`option-editor${option.isCorrect ? ' option-correct' : ''}`}>
                            <button
                                type="button"
                                className={`option-correct-btn${option.isCorrect ? ' active' : ''}${isMulti ? ' multi' : ''}`}
                                onClick={() => toggleCorrect(idx)}
                                title={isMulti ? 'Toggle correct answer' : 'Mark as correct answer'}
                            />
                            <span className="option-label-static">{option.label.toUpperCase()}</span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <LatexEditor
                                    singleLine
                                    className="option-text-input"
                                    value={option.text}
                                    onChange={v => handleOptionChange(idx, 'text', v)}
                                    placeholder="Option text..."
                                />
                                {onUploadImage && (
                                    <>
                                        {option.imageUrl && (
                                            <div className="option-image-preview">
                                                <ImageViewer src={option.imageUrl} alt={`Option ${option.label}`} />
                                                <button
                                                    type="button"
                                                    className="sui-image-crop-trigger"
                                                    onClick={() => setCropIdx(idx)}
                                                    title="Crop image"
                                                >Crop</button>
                                                {fromPaper && (
                                                    <button
                                                        type="button"
                                                        className="sui-image-crop-trigger"
                                                        onClick={() => fromPaper(option.label)}
                                                        title="Re-cut this image from the page it was extracted from"
                                                    >From paper</button>
                                                )}
                                                <button
                                                    type="button"
                                                    className="option-image-remove"
                                                    onClick={() => handleOptionChange(idx, 'imageUrl', undefined)}
                                                    title="Remove image"
                                                >x</button>
                                            </div>
                                        )}
                                        {imageOpen === idx && (
                                            <div
                                                className={`option-image-upload-zone ${imageUploading ? 'uploading' : ''}`}
                                                onPaste={e => {
                                                    const items = e.clipboardData?.items;
                                                    if (!items) return;
                                                    for (const item of items) {
                                                        if (item.type.startsWith('image/')) {
                                                            e.preventDefault();
                                                            const file = item.getAsFile();
                                                            if (file) uploadOptionImage(file, idx);
                                                            return;
                                                        }
                                                    }
                                                }}
                                                onDrop={e => {
                                                    e.preventDefault();
                                                    const file = e.dataTransfer.files?.[0];
                                                    if (file?.type.startsWith('image/')) uploadOptionImage(file, idx);
                                                }}
                                                onDragOver={e => e.preventDefault()}
                                                onClick={() => { if (!imageUploading) fileRef.current?.click(); }}
                                                tabIndex={0}
                                            >
                                                <input
                                                    ref={fileRef}
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={e => {
                                                        const file = e.target.files?.[0];
                                                        if (file) uploadOptionImage(file, idx);
                                                        e.target.value = '';
                                                    }}
                                                    style={{ display: 'none' }}
                                                />
                                                <span className="image-upload-zone-text">
                                                    {imageUploading ? 'Uploading...' : 'Paste, drop, or click to upload'}
                                                </span>
                                            </div>
                                        )}
                                        {imageOpen === idx && fromPaper && !option.imageUrl && (
                                            <button
                                                type="button"
                                                className="sui-image-crop-trigger"
                                                onClick={() => fromPaper(option.label)}
                                            >Add from paper</button>
                                        )}
                                        {imageOpen === idx && imageError && (
                                            <span className="image-upload-error">{imageError}</span>
                                        )}
                                    </>
                                )}
                            </div>
                            {onUploadImage && (
                                <button
                                    type="button"
                                    className={`option-image-btn${option.imageUrl ? ' has-image' : ''}`}
                                    onClick={() => {
                                        setImageOpen(imageOpen === idx ? null : idx);
                                        setImageError('');
                                    }}
                                    title={option.imageUrl ? 'Update option image' : 'Add option image'}
                                >
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <rect x="1" y="2" width="14" height="12" rx="2" stroke="currentColor" strokeWidth="1.5"/>
                                        <circle cx="5.5" cy="6.5" r="1.5" stroke="currentColor" strokeWidth="1.2"/>
                                        <path d="M1 11l3.5-3.5a1 1 0 011.4 0L9 10.5l1.5-1.5a1 1 0 011.4 0L15 12" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </button>
                            )}
                            <button className="btn-remove-inline" onClick={() => removeOption(idx)}>x</button>
                        </div>
                    ))}
                    {options.length === 0 && (
                        <p className="empty-note">No options. Click + Add to create.</p>
                    )}
                </div>
            )}

            {/* Crop modal — internal to this editor since cropIdx is local state. */}
            {cropIdx !== null && options[cropIdx]?.imageUrl && (
                <ImageCropModal
                    imageUrl={options[cropIdx].imageUrl!}
                    onCropped={url => { handleOptionChange(cropIdx, 'imageUrl', url); setCropIdx(null); }}
                    onClose={() => setCropIdx(null)}
                />
            )}
        </div>
    );
}
